<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Request Approval Members"
      subtitle="Manage members responsible for approving society requests"
      add-label="Add"
      :show-edit="true"
      :show-delete="true"
      @add="$router.push('/societyadmin/responsibleperson/add')"
      @edit="$router.push('/societyadmin/responsibleperson/edit/' + $event.id)"
      @delete="confirmDelete($event.id)"
      :columns="columns"
      :items="responsibleperson"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Avatar + name column -->
      <template #col-profile_url="{ item }">
        <div class="mem-avatar-wrap">
          <img
            v-if="item.profile_url"
            :src="
              API_BASE_URL + '/uploads/' + item.profile_url
            "
            :alt="item.member_name"
            class="mem-avatar-img"
            style="cursor: pointer"
            @click.stop="openPhoto(API_BASE_URL + '/uploads/' + item.profile_url, item.member_name)"
          />
          <div
            v-else
            class="mem-avatar-fallback"
            :style="{ background: primaryColor + '20', color: primaryColor }"
          >
            {{
              item.member_name ? item.member_name.charAt(0).toUpperCase() : "U"
            }}
          </div>
          <span class="mem-name">{{ item.member_name || "—" }}</span>
        </div>
      </template>

      <!-- Unit column -->
      <template #col-member_house_number="{ item }">
        <div class="house-number-badge">
               <v-icon
            size="14"
            color="#94A3B8"
            v-if="item.unit_type === '1'"
          >
            mdi-home-outline
          </v-icon>

          <v-icon
            size="14"
            color="#94A3B8"
            v-else-if="item.unit_type === '2'"
          >
            mdi-store-outline
          </v-icon>
          <span v-if="item.flat_name" class="flat-prefix"
            >{{ item.flat_name }}-</span
          >
          <span class="house-num">{{ item.member_house_number }}</span>
        </div>
      </template>

      <!-- Phone column -->
      <template #col-member_mobile_no="{ item }">
        <span class="phone-text"
          >{{ item.member_country_code }} {{ item.member_mobile_no }}</span
        >
      </template>
    </PremiumTable>

    <!-- ── Confirm Delete Modal ─────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Remove Approval Member"
      message="Are you sure you want to remove this member?"
      confirm-text="Yes, Remove"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deletePerson"
    />

    <!-- ── Media Viewer ────────────────────── -->
    <MediaViewer
      v-model="mediaViewer.open"
      :items="mediaViewer.items"
    />
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "ResponsiblePerson",
  mixins: [filterMixin],
  components: { PremiumTable, ConfirmModal, MediaViewer },

  data() {
    return {
      responsibleperson: [],
      totalRecords: 0,
      loading: false,
      deleteLoading: false,
      deleteDialog: false,
      delete_id: null,
      serverSideErrors: {},
      page: 1,
      itemsPerPage: 10,
      search: "",
      user: {},
      mediaViewer: { open: false, items: [] },
    };
  },

  computed: {
    ...mapGetters("society", ["unitLabel"]),
    ...mapState("theme", ["primaryColor"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,
    columns() {
      return [
        { key: "profile_url", label: "Member", width: "260px" },
        {
          key: "member_house_number",
          label: this.$t("responsibleperson.headers.house_no") || "Unit",
          width: "120px",
        },
        {
          key: "member_mobile_no",
          label: this.$t("responsibleperson.headers.contact_no") || "Contact",
          width: "180px",
        },
      ];
    },
  },

  watch: {
    page() {
      this.getResponsiblePerson();
    },
    itemsPerPage() {
      this.page = 1;
      this.getResponsiblePerson();
    },
    search() {
      this.page = 1;
      this.getResponsiblePerson();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    getResponsiblePerson() {
      this.loading = true;
      this.$axios
        .get("societyadmin/flatResponsiblePersons", {
          params: {
            page: this.page,
            perPage: this.itemsPerPage,
            sortBy: "created_at",
            sortDesc: true,
            search: this.search,
          },
        })
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.responsibleperson = data.person;
            this.totalRecords = data.total;
            if (data.page > data.lastPage) this.page = 1;
          } else {
            this.showError(data.message);
            if (data.type === "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deletePerson() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/flatResponsiblePersons/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getResponsiblePerson();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    closeDialog() {
      this.deleteDialog = false;
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach((e) => {
          this.serverSideErrors[e.field] = e.message;
        });
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },

    openPhoto(url, name) {
      this.mediaViewer.items = [{ url, type: "image", name: name || "Photo" }];
      this.mediaViewer.open = true;
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};

    // Initialize persistent filters
    this.initFilters("responsible_person");

    this.getResponsiblePerson();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
  padding-left: 4px;
}

.mem-avatar-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mem-avatar-img {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  min-width: 34px;
  min-height: 34px;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid var(--border, #e2e8f0);
}
.mem-avatar-fallback {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  min-width: 34px;
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.mem-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx, #111827);
}
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  /* font-weight: 700; */
  color: var(--tx, #111827);
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}
.phone-text {
  font-size: 12.5px;
  color: var(--tx2, #374151);
}
</style>
